import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { Globe, Plus, Trash2, Settings, RefreshCw, Loader2, Search, Command, LayoutDashboard } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Renders a 44x44 brand thumbnail: icon if uploaded, else an accent
// colour swatch, else a dashed placeholder square. Broken image URLs
// fall through to the accent swatch.
const BrandThumb = ({ domain }) => {
  const icon = domain.custom_icon_url || '';
  const logo = domain.custom_logo_url || '';
  const accent = domain.custom_color || '';
  const [iconOk, setIconOk] = React.useState(true);
  const src = icon || logo;
  const shouldShowImg = !!src && iconOk;
  if (shouldShowImg) {
    return (
      <div
        className="w-11 h-11 shrink-0 rounded-lg bg-white/95 border border-white/10 flex items-center justify-center overflow-hidden"
        data-testid={`brand-thumb-${domain.id}`}
      >
        <img
          src={src}
          alt=""
          className="max-w-full max-h-full object-contain"
          onError={() => setIconOk(false)}
        />
      </div>
    );
  }
  if (accent) {
    return (
      <div
        className="w-11 h-11 shrink-0 rounded-lg border border-white/10 flex items-center justify-center text-[9px] font-bold uppercase tracking-widest"
        style={{ backgroundColor: accent, color: '#ffffff', textShadow: '0 1px 2px rgba(0,0,0,0.35)' }}
        title={`Accent ${accent}`}
        data-testid={`brand-thumb-${domain.id}`}
      >
        {accent.replace('#', '').slice(0, 3).toUpperCase()}
      </div>
    );
  }
  return (
    <div
      className="w-11 h-11 shrink-0 rounded-lg border border-dashed border-white/15 flex items-center justify-center text-[9px] text-white/30 uppercase tracking-widest"
      title="No branding configured — default Yabbit chrome"
      data-testid={`brand-thumb-${domain.id}`}
    >
      —
    </div>
  );
};

// Small pill next to the domain name — "Branded" or "Default" so ops
// can filter by eye without inspecting the thumbnail closely.
const BrandChip = ({ domain }) => {
  const hasAny = !!(domain.custom_color || domain.custom_logo_url || domain.custom_icon_url);
  if (hasAny) {
    return (
      <span
        className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full text-white/90 border"
        style={{
          backgroundColor: domain.custom_color ? `${domain.custom_color}22` : 'rgba(255,255,255,0.05)',
          borderColor: domain.custom_color ? `${domain.custom_color}55` : 'rgba(255,255,255,0.15)',
        }}
        data-testid={`brand-chip-${domain.id}`}
      >
        {domain.custom_color && (
          <span
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: domain.custom_color }}
          />
        )}
        Branded
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/5 text-white/40 border border-white/10"
      data-testid={`brand-chip-${domain.id}`}
    >
      Default
    </span>
  );
};

const AdminDomainsPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [brandedOnly, setBrandedOnly] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/admin/domains`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setDomains(r.data))
      .catch(() => toast.error('Failed to load domains'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Flip a tenant between the classic flat layout and the Supervisor Hub
  // (Command Center landing + grouped nav). Takes effect on next page load
  // for that tenant's users.
  const toggleLayout = async (d) => {
    const next = (d.ui_layout === 'supervisor_hub') ? 'classic' : 'supervisor_hub';
    try {
      await axios.put(`${API}/admin/domains/${d.id}`, { ui_layout: next },
        { headers: { Authorization: `Bearer ${token}` } });
      setDomains((list) => list.map((x) => (x.id === d.id ? { ...x, ui_layout: next } : x)));
      toast.success(`${d.company?.name || d.yabbit_domain}: ${next === 'supervisor_hub' ? 'Supervisor Hub enabled' : 'back to Classic layout'}`);
    } catch {
      toast.error('Layout toggle failed');
    }
  };

  const deleteDomain = async (id, name) => {
    if (!window.confirm(`Delete domain "${name}"? This will also remove all portal users.`)) return;
    try {
      await axios.delete(`${API}/admin/domains/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDomains(d => d.filter(x => x.id !== id));
      toast.success('Domain deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  const filtered = domains.filter(d => {
    if (search) {
      const q = search.toLowerCase();
      const matches = d.yabbit_domain?.toLowerCase().includes(q)
        || d.company?.name?.toLowerCase().includes(q);
      if (!matches) return false;
    }
    if (brandedOnly) {
      const hasBrand = !!(d.custom_color || d.custom_logo_url || d.custom_icon_url);
      if (!hasBrand) return false;
    }
    return true;
  });
  const brandedCount = domains.filter(d => d.custom_color || d.custom_logo_url || d.custom_icon_url).length;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Domains</h1>
            <p className="text-white/40 text-sm mt-1">{domains.length} customer {domains.length === 1 ? 'domain' : 'domains'} configured</p>
          </div>
          <button
            onClick={() => navigate('/admin/domains/new')}
            className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-400 text-white text-sm font-medium rounded-lg transition-colors"
            data-testid="new-domain-btn"
          >
            <Plus className="w-4 h-4" />
            Add Domain
          </button>
        </div>

        {/* Search + brand filter */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search domains..."
              className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-4 py-2.5 text-white text-sm placeholder-white/20 focus:outline-none focus:border-teal-500/50"
              data-testid="domain-search-input"
            />
          </div>
          <label
            className={`inline-flex items-center gap-2 cursor-pointer select-none text-sm rounded-xl border px-3 py-2.5 transition-colors ${
              brandedOnly
                ? 'bg-teal-500/15 border-teal-500/40 text-teal-100'
                : 'bg-white/5 border-white/10 text-white/60 hover:border-white/20'
            }`}
            data-testid="branded-only-toggle"
          >
            <input
              type="checkbox"
              checked={brandedOnly}
              onChange={(e) => setBrandedOnly(e.target.checked)}
              className="sr-only"
            />
            <span className={`w-4 h-4 rounded border flex items-center justify-center text-[10px] ${
              brandedOnly ? 'bg-teal-500 border-teal-400 text-white' : 'border-white/20'
            }`}>
              {brandedOnly ? '✓' : ''}
            </span>
            Branded only
            <span className="text-xs text-white/40 ml-1">({brandedCount}/{domains.length})</span>
          </label>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 text-white/20 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24">
            <Globe className="w-12 h-12 text-white/10 mx-auto mb-3" />
            <p className="text-white/30 mb-4">{search ? 'No domains match your search' : 'No domains configured yet'}</p>
            {!search && (
              <button
                onClick={() => navigate('/admin/domains/new')}
                className="text-teal-400 hover:text-teal-300 text-sm"
                data-testid="first-domain-btn"
              >
                Add your first domain →
              </button>
            )}
          </div>
        ) : (
          <div className="grid gap-3">
            {filtered.map(d => (
              <div
                key={d.id}
                className="bg-white/5 border border-white/10 rounded-xl p-5 hover:border-white/20 transition-colors"
                data-testid={`domain-card-${d.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 flex items-start gap-3 cursor-pointer min-w-0" onClick={() => navigate(`/admin/domains/${d.id}`)}>
                    {/* Brand preview thumbnail — spec: spot un-branded vs
                        branded at a glance. Renders one of three states:
                        (a) tenant icon if uploaded, (b) accent-colour
                        swatch if only accent set, (c) dashed grey square
                        with "no brand" hint if nothing configured. */}
                    <BrandThumb domain={d} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1 flex-wrap">
                        <h3 className="text-white font-semibold">{d.company?.name || d.yabbit_domain}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          d.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-white/10 text-white/30'
                        }`}>{d.status}</span>
                        <BrandChip domain={d} />
                      </div>
                      <p className="text-white/30 text-sm">{d.yabbit_domain}</p>
                      <div className="flex flex-wrap gap-4 mt-3 text-sm text-white/40">
                        <span className="flex items-center gap-1">
                          <Globe className="w-3 h-3" /> {d.industry?.replace(/_/g, ' ')}
                        </span>
                        <span>{d.extensions_monitored?.length || 0} extensions</span>
                        <span>{d.total_calls || 0} calls</span>
                        {d.last_sync && <span>Last sync: {new Date(d.last_sync).toLocaleDateString()}</span>}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => toggleLayout(d)}
                      className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-lg border transition-colors ${
                        d.ui_layout === 'supervisor_hub'
                          ? 'bg-teal-500/15 border-teal-500/40 text-teal-200 hover:bg-teal-500/25'
                          : 'bg-white/5 border-white/10 text-white/50 hover:border-white/25'
                      }`}
                      title={d.ui_layout === 'supervisor_hub'
                        ? 'Supervisor Hub layout active — click to revert this tenant to Classic'
                        : 'Classic layout active — click to enable the Supervisor Hub for this tenant'}
                      data-testid={`layout-toggle-${d.id}`}
                    >
                      {d.ui_layout === 'supervisor_hub'
                        ? <><Command className="w-3.5 h-3.5" /> Hub</>
                        : <><LayoutDashboard className="w-3.5 h-3.5" /> Classic</>}
                    </button>
                    <button
                      onClick={() => navigate(`/admin/domains/${d.id}/edit`)}
                      className="p-2 text-white/30 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                      data-testid={`edit-domain-${d.id}`}
                    >
                      <Settings className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteDomain(d.id, d.company?.name || d.yabbit_domain)}
                      className="p-2 text-white/30 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                      data-testid={`delete-domain-${d.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDomainsPage;
